import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  alpha,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { THEME_COLORS } from '../shared/constants/theme';
import { getDashboardDarkSurfaceTokens } from './dashboardSurfaceSx';
import { getDistributionSectionIconBoxSx } from './distributionMobileLayout';
import { getBookingDrawerMetaChipSx, BOOKING_DRAWER_ACCENT } from './bookingDrawerLayout';

export const DISTRIBUTION_ACCENT = THEME_COLORS.PRIMARY;

export function useDistributionCardTheme(isDark) {
  return {
    isDark,
    accent: DISTRIBUTION_ACCENT,
    tokens: getDashboardDarkSurfaceTokens(isDark),
  };
}

export function DistributionCardShell({ isDark, children, maxHeight = 520 }) {
  const tokens = getDashboardDarkSurfaceTokens(isDark);
  return (
    <Card
      elevation={0}
      sx={{
        width: '100%',
        height: '100%',
        maxHeight,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        background: isDark ? tokens.surfaceGradient : THEME_COLORS.CARD_BACKGROUND_LIGHT,
        backgroundColor: isDark ? THEME_COLORS.CARD_BACKGROUND_DARK : THEME_COLORS.CARD_BACKGROUND_LIGHT,
        border: `1px solid ${alpha(DISTRIBUTION_ACCENT, isDark ? 0.22 : 0.16)}`,
        boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : '0 1px 3px rgba(0,0,0,0.06)',
        transition: 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.28s ease, border-color 0.28s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: alpha(DISTRIBUTION_ACCENT, isDark ? 0.42 : 0.32),
          boxShadow: isDark
            ? `0 16px 32px rgba(0,0,0,0.35), 0 0 0 1px ${alpha(DISTRIBUTION_ACCENT, 0.12)}`
            : `0 12px 24px ${alpha(DISTRIBUTION_ACCENT, 0.16)}`,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${DISTRIBUTION_ACCENT} 0%, ${THEME_COLORS.PRIMARY_HOVER} 100%)`,
        },
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          p: 2.25,
          pt: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.75,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {children}
      </CardContent>
    </Card>
  );
}

export function DistributionChannelHeader({ channel, isDark, icon: Icon, actions }) {
  const tokens = getDashboardDarkSurfaceTokens(isDark);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexShrink: 0 }}>
      <Box sx={getDistributionSectionIconBoxSx(DISTRIBUTION_ACCENT, false)}>
        <Icon sx={{ fontSize: 22, color: DISTRIBUTION_ACCENT }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, pt: 0.15 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            fontSize: '1rem',
            lineHeight: 1.25,
            color: isDark ? THEME_COLORS.TEXT_PRIMARY_DARK : THEME_COLORS.TEXT_PRIMARY_LIGHT,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            mb: 0.75,
          }}
          title={channel.name}
        >
          {channel.name}
        </Typography>
        <Chip
          label={channel.type_display || channel.type}
          size="small"
          sx={{
            ...getBookingDrawerMetaChipSx(isDark),
            height: 22,
            fontSize: '0.68rem',
            '& .MuiChip-label': { px: 1 },
          }}
        />
      </Box>
      {channel.image_path?.trim() ? (
        <Box
          sx={{
            flexShrink: 0,
            width: 56,
            height: 56,
            p: 0.75,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: isDark ? tokens.chipBg : THEME_COLORS.CARD_BACKGROUND_LIGHT,
            border: `1px solid ${tokens.surfaceBorder}`,
            boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.06)',
          }}
        >
          <img
            src={channel.image_path}
            alt={channel.name}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </Box>
      ) : null}
      {actions}
    </Box>
  );
}

export function DistributionSimpleHeader({ isDark, icon: Icon, title, chipLabel }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexShrink: 0 }}>
      <Box sx={getDistributionSectionIconBoxSx(DISTRIBUTION_ACCENT, false)}>
        <Icon sx={{ fontSize: 22, color: DISTRIBUTION_ACCENT }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, pt: 0.15 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            fontSize: '1rem',
            lineHeight: 1.25,
            color: isDark ? THEME_COLORS.TEXT_PRIMARY_DARK : THEME_COLORS.TEXT_PRIMARY_LIGHT,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            mb: chipLabel ? 0.75 : 0,
          }}
          title={title}
        >
          {title}
        </Typography>
        {chipLabel ? (
          <Chip
            label={chipLabel}
            size="small"
            sx={{
              ...getBookingDrawerMetaChipSx(isDark),
              height: 22,
              fontSize: '0.68rem',
              '& .MuiChip-label': { px: 1 },
            }}
          />
        ) : null}
      </Box>
    </Box>
  );
}

export function DistributionListSection({ isDark, title, badge, children }) {
  const tokens = getDashboardDarkSurfaceTokens(isDark);
  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25, flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: tokens.text, fontSize: '0.8125rem' }}>
          {title}
        </Typography>
        {badge ? (
          <Chip
            size="small"
            label={badge}
            sx={{
              height: 22,
              fontSize: '0.65rem',
              fontWeight: 700,
              bgcolor: alpha(DISTRIBUTION_ACCENT, isDark ? 0.1 : 0.08),
              color: DISTRIBUTION_ACCENT,
              border: `1px solid ${alpha(DISTRIBUTION_ACCENT, 0.22)}`,
            }}
          />
        ) : null}
      </Stack>
      <Stack
        spacing={0.85}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          pr: 0.5,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-track': {
            backgroundColor: isDark ? tokens.chipBg : alpha(DISTRIBUTION_ACCENT, 0.05),
            borderRadius: 3,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(DISTRIBUTION_ACCENT, isDark ? 0.35 : 0.28),
            borderRadius: 3,
          },
        }}
      >
        {children}
      </Stack>
    </Box>
  );
}

export function DistributionListRow({ isDark, children, leading, onEdit, onDelete, deleteTooltip = 'Delete' }) {
  const tokens = getDashboardDarkSurfaceTokens(isDark);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: 1.15,
        borderRadius: 1.5,
        backgroundColor: isDark ? tokens.chipBg : alpha(DISTRIBUTION_ACCENT, 0.04),
        border: `1px solid ${tokens.surfaceBorder}`,
        transition: 'border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          borderColor: alpha(DISTRIBUTION_ACCENT, isDark ? 0.32 : 0.28),
          transform: 'translateX(2px)',
          boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.2)' : `0 4px 12px ${alpha(DISTRIBUTION_ACCENT, 0.1)}`,
        },
      }}
    >
      {leading}
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      {(onEdit || onDelete) && (
        <DistributionActionPill isDark={isDark} onEdit={onEdit} onDelete={onDelete} deleteTooltip={deleteTooltip} />
      )}
    </Box>
  );
}

export function DistributionMetricTile({ isDark, label, value, icon: Icon }) {
  const tokens = getDashboardDarkSurfaceTokens(isDark);
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 1.5,
        backgroundColor: isDark ? tokens.chipBg : alpha(DISTRIBUTION_ACCENT, 0.05),
        border: `1px solid ${tokens.surfaceBorder}`,
        flex: 1,
      }}
    >
      {Icon ? (
        <Icon sx={{ fontSize: 16, color: DISTRIBUTION_ACCENT, mb: 0.5 }} />
      ) : null}
      <Typography variant="caption" sx={{ color: tokens.subtleText, fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color: tokens.text, fontSize: '0.8125rem', mt: 0.25 }}>
        {value}
      </Typography>
    </Box>
  );
}

export function DistributionActionPill({ isDark, onEdit, onDelete, deleteTooltip = 'Delete' }) {
  const tokens = getDashboardDarkSurfaceTokens(isDark);
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.35,
        flexShrink: 0,
        p: 0.25,
        borderRadius: 999,
        bgcolor: isDark ? alpha('#ffffff', 0.03) : alpha(DISTRIBUTION_ACCENT, 0.05),
        border: `1px solid ${tokens.surfaceBorder}`,
      }}
    >
      {onEdit ? (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={onEdit}
            sx={{ color: BOOKING_DRAWER_ACCENT, width: 30, height: 30, '&:hover': { bgcolor: alpha(DISTRIBUTION_ACCENT, 0.14) } }}
          >
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : null}
      {onDelete ? (
        <Tooltip title={deleteTooltip}>
          <IconButton
            size="small"
            onClick={onDelete}
            sx={{ color: 'error.main', width: 30, height: 30, '&:hover': { bgcolor: alpha('#ef4444', 0.12) } }}
          >
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
}

export function DistributionTotalBanner({ isDark, label, value }) {
  const tokens = getDashboardDarkSurfaceTokens(isDark);
  return (
    <Box
      sx={{
        flexShrink: 0,
        p: 1.35,
        borderRadius: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: isDark
          ? `linear-gradient(90deg, ${alpha(DISTRIBUTION_ACCENT, 0.12)} 0%, ${alpha(DISTRIBUTION_ACCENT, 0.04)} 100%)`
          : `linear-gradient(90deg, ${alpha(DISTRIBUTION_ACCENT, 0.1)} 0%, ${alpha(DISTRIBUTION_ACCENT, 0.03)} 100%)`,
        border: `1px solid ${alpha(DISTRIBUTION_ACCENT, isDark ? 0.24 : 0.18)}`,
      }}
    >
      <Typography variant="body2" sx={{ color: tokens.subtleText, fontWeight: 600, fontSize: '0.8125rem' }}>
        {label}
      </Typography>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 800,
          color: DISTRIBUTION_ACCENT,
          fontSize: '1.25rem',
          lineHeight: 1,
          textShadow: isDark ? `0 0 20px ${alpha(DISTRIBUTION_ACCENT, 0.25)}` : 'none',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export function DistributionFooterActions({ isDark, onDelete, deleteTooltip = 'Delete' }) {
  return (
    <Box
      sx={{
        flexShrink: 0,
        pt: 1.5,
        display: 'flex',
        justifyContent: 'flex-end',
        borderTop: `1px solid ${isDark ? THEME_COLORS.DIVIDER_DARK : THEME_COLORS.DIVIDER_LIGHT}`,
      }}
    >
      <DistributionActionPill isDark={isDark} onDelete={onDelete} deleteTooltip={deleteTooltip} />
    </Box>
  );
}
